import type { BookingValueOddsTypeLimit } from '@/types/booked-bet';
import { OperationType } from '@/types/booked-bet';
import BookingStrategy from './booking-strategy';
import { WebOddsType, IncDecType } from '@nf/utils-common/constants';
import { idealRound } from '@nf/utils-common/numbers';
import { matchEquals } from '@nf/utils-common/fpw/monadic';
import { decimalOdds, decimalOddsToMyOdds } from '@/utils/odds-utils';

class MainBookingStrategy extends BookingStrategy {
	generateStepSize(value: number) {
		return 0.01;
	}

	getOddsTypeLimit(value: number): BookingValueOddsTypeLimit {
		const isPositiveOrZero = value >= 0;

		const limit: BookingValueOddsTypeLimit = 
			matchEquals(this.options.oddsType)
				.on(WebOddsType.MY, () => ({
					min: isPositiveOrZero ? 0.01 : undefined,
					max: isPositiveOrZero ? undefined : -0.01
				}))
				.on(WebOddsType.DEC, () => ({
					min: 1.01,
					max: 101
				}))
				.otherwise(() => ({
					min: undefined, 
					max: undefined
				}));

		return limit;
	}

	calculateDefaultRange(value: number, incDecType: IncDecType): Array<number> {
		const isIncrement = incDecType === IncDecType.Increment;

		let rangeValues: Array<number> = matchEquals(this.options.oddsType)
			.on(WebOddsType.MY, () => {
				return isIncrement
					? this.generateIncrementedValuesForMalay(value, this.oddsRangeSize)
					: this.generateDecrementedValuesForMalay(value, this.oddsRangeSize);
			})
			.on(WebOddsType.DEC, () => {
				return isIncrement
					? this.generateIncrementedValuesForDecimal(value, this.oddsRangeSize)
					: this.generateDecrementedValuesForDecimal(value, this.oddsRangeSize);
			})
			.otherwise(() => []);
		rangeValues = rangeValues
			.map(value => idealRound(value, 2))
			.filter(value => value !== 0);
			
		return rangeValues;
	}

	calculateBookingOdds(
		odds: number, 
		operationType: OperationType, 
		baseOddsType?: WebOddsType
	): number {
		let value = odds;
		value = this.parseValueByOddsType(odds);
		value = this.calculateWithStep(value, operationType);
		// Adjusting edge value according to different odds types
		value = this.adjustEdgeValueByOddsType(value, operationType, baseOddsType);
		// Because the DEC odds were converted to MY odds in the previous step, 
		// they must be reverted to decimal odds here
		value = this.info.isDecimalOdds ? decimalOdds(value) : value;
		return value;
	}

	private parseValueByOddsType(value: number) {
		// the decimal odds need to be converted to MY odds
		if (this.isMainDecimalOdds) {
			return decimalOddsToMyOdds(value);
		}
		return value;
	};

	private adjustEdgeValueByOddsType(
		value: number, 
		operationType: OperationType, 
		baseOddsType?: WebOddsType
	): number {
		const oddsType = baseOddsType ?? this.options.oddsType;

		const operationStrategies = {
			[OperationType.Plus]: (value: number) => {
				if (value > 1) {
					return -0.99;
				}
				return value;
			},
			[OperationType.Minus]: (value: number) => {
				if (value === -1) {
					return 1;
				}
				return value;
			},
		};

		if (oddsType === WebOddsType.MY) {
			return operationStrategies[operationType](value);
		}

		return value;
	}

	private generateIncrementedValuesForMalay(start: number, count: number): Array<number> {
		let currentValue = start;
		const result = [];
	
		for (let i = 0; i < count; i++) {
			currentValue = idealRound(currentValue + 0.01, 2);
	
			if (currentValue > 1 || currentValue <= -1) {
				currentValue = -0.99;
			} else if (currentValue === 0) {
				break;
			}
	
			result.push(currentValue);
		}
	
		return result;
	}

	private generateDecrementedValuesForMalay(start: number, count: number): Array<number> {
		let currentValue = start;
		const result = [];
	
		for (let i = 0; i < count; i++) {
			currentValue = idealRound(currentValue - 0.01, 2);
	
			if (currentValue === -1) {
				currentValue = 1;
			} else if (currentValue === 0) {
				break;
			} else if (currentValue < -1) {
				currentValue = 0.99;
			}
	
			result.push(currentValue);
		}
	
		result.sort((a, b) => a - b);
		return result;
	}
}

export default MainBookingStrategy;